import PageBackTitle from "../Component/PageBackTitle";
import React, { useEffect, useRef, useState } from "react";
import "./User.css";
import "./Button.css";
import "../forms/UserForm.css";
import "../BarrelManagement/PurchaseIndents/purchaseIndents.css";
import "../SafetyObservation/safetyObservation.css";
import "../HsePolicies/hsePolicies.css";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { HiOutlinePlus } from "react-icons/hi";
import { IoSearchOutline } from "react-icons/io5";
import { TbRefresh } from "react-icons/tb";
import { MdExpandMore, MdOutlineModeEditOutline } from "react-icons/md";
import { FiTrash2 } from "react-icons/fi";
import API_URL from "../../src/Config";
import Pagination from "../Component/Pagination";
import {
  formatDateDisplay,
  parseApiError,
} from "../SafetyObservation/observationUtils";
const INITIAL_FILTERS = {};

function HsePoliciesList({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const [policies, setPolicies] = useState([]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [policyToDelete, setPolicyToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [paginationKey, setPaginationKey] = useState(0);
  const popupRef = useRef(null);
  const hasActiveFilters = Boolean(searchInput.trim());

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const fetchPolicies = async (
    currentPage,
    currentItemsPerPage,
    activeFilters,
  ) => {
    try {
      setLoading(true);
      const authKey = localStorage.getItem("authKey");
      const params = {
        page: currentPage,
        per_page: currentItemsPerPage,
      };

      if (searchInput.trim()) {
        params.search = searchInput.trim();
      }

      const response = await axios.get(`${API_URL}/api/safety/hse-policies`, {
        headers: {
          Authorization: authKey,
        },
        params,
      });

      setPolicies(response.data.items || []);
      setTotalItems(response.data.total || 0);
    } catch (error) {
      showSnackbar(parseApiError(error, "Failed to fetch policies"), "error");
      setPolicies([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPolicies(page, itemsPerPage, filters);
  }, [page, itemsPerPage, filters, searchInput]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setSelectedRowIndex(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handlePaginationChange = (currentPage, nextItemsPerPage) => {
    setPage(currentPage);
    setItemsPerPage(nextItemsPerPage);
  };

  const handleRefresh = () => {
    if (!hasActiveFilters) {
      fetchPolicies(page, itemsPerPage, filters);
      return;
    }

    setFilters(INITIAL_FILTERS);
    setSearchInput("");
    setPage(1);
    setPaginationKey((prevState) => prevState + 1);
  };

  const handleSearchInputChange = (event) => {
    setSearchInput(event.target.value);
    setPage(1);
    setPaginationKey((prevState) => prevState + 1);
  };

  const handleButtonClick = (index, event) => {
    event.stopPropagation();
    setSelectedRowIndex((prevState) => (prevState === index ? null : index));
  };

  const handleDeletePolicy = async () => {
    if (!policyToDelete) return;

    try {
      setIsDeleting(true);
      const authKey = localStorage.getItem("authKey");

      const response = await axios.delete(
        `${API_URL}/api/safety/hse-policies/${policyToDelete.id}`,
        {
          headers: {
            Authorization: authKey,
          },
        },
      );

      showSnackbar(response.data?.message || "Policy deleted successfully");
      const nextPage = policies.length === 1 && page > 1 ? page - 1 : page;
      setPolicyToDelete(null);

      if (nextPage !== page) {
        setPage(nextPage);
      } else {
        fetchPolicies(nextPage, itemsPerPage, filters);
      }
    } catch (error) {
      showSnackbar(parseApiError(error, "Failed to delete policy"), "error");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div
        className={`data-list-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-list-content">
          <div className="data-list-header">
            <div className="data-list-header-content">
              <PageBackTitle>HSE Policies List</PageBackTitle>
              <div className="data-list-heading-content-right-side observation-header-actions">
                <div className="search-bar-container">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchInput}
                    onChange={handleSearchInputChange}
                    className="search-bar"
                  />
                  <IoSearchOutline className="search-bar-icon" />
                </div>

                <Link to="/hse-policies-form" className="data-list-new">
                  <Tooltip title="New HSE Policy" arrow>
                    <button className="new-button">
                      <HiOutlinePlus className="new-plus-icon" /> New
                    </button>
                  </Tooltip>
                </Link>

                <Tooltip title="Refresh List" arrow>
                  <button className="refresh-button" onClick={handleRefresh}>
                    <TbRefresh className="refresh-icon" />
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>

          <div className="header-separator"></div>

          <div className="data-list-body">
            <div className="data-list-scroll-body">
              <div className="data-list-table">
                {loading ? (
                  <div className="loader-container">
                    <div className="loader"></div>
                    <div>Loading...</div>
                  </div>
                ) : policies.length > 0 ? (
                  <table className="table multilevel-data">
                    <thead>
                      <tr>
                        <th className="center-alignment">SR. NO.</th>
                        <th>TITLE</th>
                        <th>CATEGORY</th>
                        <th>EFFECTIVE DATE</th>
                        {/* <th>DESCRIPTION</th> */}
                        <th>CREATED BY</th>
                        <th>ACK STATUS</th>
                        <th className="center-alignment">ACTION</th>
                      </tr>
                    </thead>
                    <tbody className="data-list-scroll-bodys">
                      {policies.map((policy, index) => (
                        <tr key={policy.id} className="hoverable-row">
                          <td className="center-alignment">
                            {(page - 1) * itemsPerPage + index + 1}
                          </td>
                          <td>{policy.title || "NA"}</td>
                          <td>{policy.category || "NA"}</td>
                          <td>{formatDateDisplay(policy.effective_date)}</td>
                          {/* <td className="hse-policy-description-preview">
                            {getPolicyDescriptionPreview(policy.description)}
                          </td> */}
                          <td>{policy.created_by_name || "NA"}</td>
                          <td>
                            <span
                              className={`hse-policy-ack-status ${
                                Number(policy.acknowledged_by_me) > 0
                                  ? "acknowledged"
                                  : "not-acknowledged"
                              }`}
                            >
                              {Number(policy.acknowledged_by_me) > 0
                                ? "Acknowledged"
                                : "Pending"}
                            </span>
                          </td>
                          <td className="center-alignment">
                            <div className="observation-action-buttons">
                              <button
                                className="button-bg"
                                onClick={() =>
                                  navigate(`/hse-policies-view/${policy.id}`)
                                }
                              >
                                {Number(policy.acknowledged_by_me) > 0
                                  ? "View"
                                  : "Review"}
                              </button>
                              {/* <button
                                className="observation-icon-button observation-icon-button-danger"
                                onClick={() => setPolicyToDelete(policy)}
                                type="button"
                              >
                                <FiTrash2 />
                              </button> */}
                              <button
                                className="edit-data-button"
                                onClick={(event) =>
                                  handleButtonClick(index, event)
                                }
                              >
                                <div className="edit-button">
                                  <MdExpandMore />
                                </div>
                              </button>
                            </div>
                            {selectedRowIndex === index && (
                              <div
                                className="edit-popup"
                                style={{ top: "58%", right: "4%" }}
                                ref={popupRef}
                              >
                                <div className="edit-popup-contant">
                                  <div className="edit-popup-button">
                                    <Link
                                      to={`/edit-hse-policy/${policy.id}`}
                                      className="inquiry-new"
                                    >
                                      <button className="edit-button-inquiry">
                                        <MdOutlineModeEditOutline /> Edit
                                      </button>
                                    </Link>
                                  </div>
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-users-container">
                    <div className="no-users-message">
                      No HSE Policies Found
                    </div>
                  </div>
                )}
              </div>

              {totalItems > 0 && (
                <Pagination
                  key={paginationKey}
                  showPerPage={itemsPerPage}
                  total={totalItems}
                  onPaginationChange={handlePaginationChange}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {policyToDelete && (
        <div className="confirm-delete-popup">
          <div className="confirm-delete-popup-content">
            <div className="confirm-model">
              <div className="confirm-model-content">
                <div className="confirm-model-content-body">
                  <p>
                    Are you sure you want to delete policy{" "}
                    <strong>{policyToDelete.title || "this item"}</strong>?
                  </p>
                </div>
                <div className="confirm-model-content-footer">
                  <button
                    className="confirm-delete-button"
                    onClick={handleDeletePolicy}
                    disabled={isDeleting}
                  >
                    {isDeleting ? "Deleting..." : "Yes"}
                  </button>
                  <button
                    className="confirm-cancel-button"
                    onClick={() => !isDeleting && setPolicyToDelete(null)}
                    disabled={isDeleting}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default HsePoliciesList;
